import { ChevronDownIcon, type LucideIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type TicketSectionTone = 'navy' | 'steel' | 'amber' | 'red' | 'emerald' | 'muted';

const toneStyles: Record<
  TicketSectionTone,
  { card: string; header: string; title: string; icon: string }
> = {
  navy: {
    card: 'border-l-[3px] border-l-status-info-foreground',
    header: 'bg-status-info',
    title: 'text-status-info-foreground',
    icon: 'text-status-info-foreground',
  },
  steel: {
    card: 'border-l-[3px] border-l-status-neutral-border',
    header: 'bg-muted',
    title: 'text-foreground',
    icon: 'text-muted-foreground',
  },
  amber: {
    card: 'border-l-[3px] border-l-status-warning-foreground',
    header: 'bg-status-warning',
    title: 'text-status-warning-foreground',
    icon: 'text-status-warning-foreground',
  },
  red: {
    card: 'border-l-[3px] border-l-status-danger-foreground',
    header: 'bg-status-danger',
    title: 'text-status-danger-foreground',
    icon: 'text-status-danger-foreground',
  },
  emerald: {
    card: 'border-l-[3px] border-l-status-success-foreground',
    header: 'bg-status-success',
    title: 'text-status-success-foreground',
    icon: 'text-status-success-foreground',
  },
  muted: {
    card: 'border-l-[3px] border-l-border',
    header: 'bg-muted/60',
    title: 'text-foreground',
    icon: 'text-muted-foreground',
  },
};

export function TicketSectionCard({
  tone = 'muted',
  title,
  toggleLabel,
  description,
  icon: Icon,
  action,
  children,
  className,
  contentClassName,
  collapsible = false,
  defaultOpen = true,
  id,
}: {
  tone?: TicketSectionTone;
  title: ReactNode;
  /** Accessible name of the collapse control when `title` is not a plain string. */
  toggleLabel?: string;
  description?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  id?: string;
}) {
  const styles = toneStyles[tone];
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  const accessibleName = typeof title === 'string' ? title : toggleLabel;

  const heading = (
    <>
      {Icon ? <Icon className={cn('size-4 shrink-0', styles.icon)} aria-hidden /> : null}
      <span
        className="min-w-0 flex-1"
        aria-hidden={collapsible && Boolean(accessibleName) ? true : undefined}
      >
        {title}
      </span>
    </>
  );

  return (
    <Card id={id} className={cn('gap-0 overflow-hidden py-0 shadow-sm', styles.card, className)}>
      <CardHeader
        className={cn(
          'gap-1.5 border-b px-5 py-4',
          styles.header,
          collapsible && !open && 'border-b-0',
        )}
      >
        <div className="flex items-start gap-2">
          {collapsible ? (
            <button
              type="button"
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
              )}
              aria-expanded={open}
              aria-controls={contentId}
              aria-label={accessibleName}
              onClick={() => setOpen((value) => !value)}
            >
              <CardTitle
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-2 text-[0.95rem]',
                  styles.title,
                )}
              >
                {heading}
              </CardTitle>
              <ChevronDownIcon
                className={cn(
                  'size-4 shrink-0 transition-transform',
                  open && 'rotate-180',
                  styles.icon,
                )}
                aria-hidden
              />
            </button>
          ) : (
            <CardTitle
              className={cn('flex min-w-0 flex-1 items-center gap-2 text-[0.95rem]', styles.title)}
            >
              {heading}
            </CardTitle>
          )}
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
        {description ? (
          <CardDescription className="text-[0.8125rem] leading-snug">{description}</CardDescription>
        ) : null}
      </CardHeader>
      {collapsible ? (
        <CardContent
          id={contentId}
          hidden={!open}
          className={cn('px-5 py-4', !open && 'hidden', contentClassName)}
        >
          {open ? children : null}
        </CardContent>
      ) : children != null ? (
        <CardContent className={cn('px-5 py-4', contentClassName)}>{children}</CardContent>
      ) : null}
    </Card>
  );
}
